import React from "react";
import { ResponseEnum, ResponseI, ModuleI } from "../types";
import Modal from "./Modal";
import Carousel from "./Carousel";
import QuestionAnswerDisplay, { ContentEntry } from "./QuestionAnswerDisplay";

interface ResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  response?: ResponseI;
  responses?: ResponseI[];
  currentResponseIndex?: number;
  onResponseChange?: (index: number) => void;
  content?: Record<string, ContentEntry>;
  footer?: React.ReactNode;
  showTopicInfo?: boolean;
}

export default function ResponseModal({
  isOpen,
  onClose,
  title,
  response,
  responses,
  currentResponseIndex = 0,
  onResponseChange,
  content,
  footer,

  showTopicInfo = false,
}: ResponseModalProps) {
  const currentResponse =
    response || (responses && responses[currentResponseIndex]);
  const formattedDate = currentResponse
    ? new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(currentResponse.createdAt))
    : "";

  const renderContent = () => {
    if (currentResponse?.type === ResponseEnum.REFLECTION) {
      return (
        <div className="space-y-3">
          <div className="flex justify-start">
            <div className="bg-electrum-50 rounded-lg p-3 max-w-[80%]">
              <p className="font-medium text-sm whitespace-normal break-words">
                Reflection
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-electrum-100 rounded-lg p-3 max-w-[80%]">
              <p className="text-sm whitespace-pre-wrap">
                {currentResponse.content?.body || "No content"}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Use provided content or response content
    const contentToDisplay = content || currentResponse?.content;

    return <QuestionAnswerDisplay content={contentToDisplay} />;
  };

  return (
    <Modal
      title={title || currentResponse?.title || "Response"}
      isOpen={isOpen}
      onCloseModal={onClose}
      height="fit-content"
      maxHeight="80vh"
      size="lg"
      backgroundColor="bg-gray-100"
      footer={footer}
    >
      <div className="h-96 flex flex-col bg-gray-100">
        {responses && responses.length > 1 ? (
          <div className="flex-1 p-2 overflow-hidden">
            <Carousel
              showButtonsUp={false}
              showButtonsDown={true}
              paginationType="number"
              className="w-full h-full"
              onSlideChange={onResponseChange}
            >
              {responses.map((response) => (
                <div key={response._id} className="h-full overflow-y-auto">
                  <div className="space-y-3 p-2 bg-gray-100">
                    <div className="space-y-3 bg-white p-4 rounded-lg h-72 overflow-y-auto">
                      {renderContent()}
                    </div>
                    {formattedDate && (
                      <div className="text-sm text-gray-600 text-center">
                        {formattedDate}
                      </div>
                    )}
                    {showTopicInfo &&
                      response?.content &&
                      "module" in response.content && (
                        <div className="mt-4 p-3 bg-electrum-50 rounded-lg">
                          <div className="text-sm font-medium mb-1">Topic:</div>
                          <p className="font-semibold">
                            {(response.content.module as ModuleI).title ||
                              "No topic"}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        ) : (
          <div className="space-y-3 p-2 bg-gray-100">
            <div className="space-y-3 bg-white p-4 rounded-lg h-72 overflow-y-auto">
              {renderContent()}
            </div>
            {formattedDate && (
              <div className="text-sm text-gray-600 text-center">
                {formattedDate}
              </div>
            )}
            {showTopicInfo &&
              currentResponse?.content &&
              "module" in currentResponse.content && (
                <div className="mt-4 p-3 bg-electrum-50 rounded-lg">
                  <div className="text-sm font-medium mb-1">Topic:</div>
                  <p className="font-semibold">
                    {(currentResponse.content.module as ModuleI).title ||
                      "No topic"}
                  </p>
                </div>
              )}
          </div>
        )}
      </div>
    </Modal>
  );
}
